import { hash } from 'immutable';
import { handleActions } from 'redux-actions';

import { resourceUpserters } from 'app-components/flux/core';
import { coreActions } from 'app-components/flux/core-actions';

import * as actions from './actions';
import type { FundingAccountProps, FundingAccountStore, FundingAccountStoreProps } from './types';
import { FUNDING_ACCOUNTS_REDUCER_KEY, mkFundingAccount, mkFundingAccountStore } from './types';

const fundingAccountsReducer = handleActions<
  FundingAccountStore,
  | coreActions.SanitizeAction
  | ReturnType<typeof actions.getFundingAccounts>
  | ReturnType<typeof actions.getFundingAccountsSuccess>
  | ReturnType<typeof actions.getFundingAccountsFailure>
  | ReturnType<typeof actions.createFundingAccountSuccess>
  | ReturnType<typeof actions.createFundingAccountFailure>
  | ReturnType<typeof actions.deleteFundingAccountSuccess>
>(
  {
    [coreActions.SANITIZE]: (state) =>
      state.set(
        'resourcesById',
        state.resourcesById.map((resource) =>
          resource.mergeDeep(
            mkFundingAccount({
              routingNumber: hash(resource?.routingNumber).toString(),
              accountNumber: hash(resource?.accountNumber).toString(),
              nickname: hash(resource?.nickname).toString(),
            }),
          ),
        ),
      ),

    [actions.getFundingAccounts.type]: (state) => state.merge({ isLoading: true }),

    [actions.getFundingAccountsSuccess.type]: (state, action) => {
      const { payload } = action as unknown as ReturnType<typeof actions.getFundingAccountsSuccess>;
      return resourceUpserters.upsertResources<FundingAccountProps, FundingAccountStoreProps>(state, payload);
    },

    [actions.getFundingAccountsFailure.type]: (state, action) => {
      const { payload } = action as ReturnType<typeof actions.getFundingAccountsFailure>;
      return resourceUpserters.completeWithError<FundingAccountProps, FundingAccountStoreProps>(state, payload);
    },

    [actions.createFundingAccountSuccess.type]: (state, action) => {
      const { payload } = action as ReturnType<typeof actions.createFundingAccountSuccess>;
      return resourceUpserters.upsertResource<FundingAccountProps, FundingAccountStoreProps>(state, payload);
    },

    [actions.deleteFundingAccountSuccess.type]: (state, action) => {
      const { payload } = action as ReturnType<typeof actions.deleteFundingAccountSuccess>;
      return resourceUpserters.upsertResource<FundingAccountProps, FundingAccountStoreProps>(state, payload);
    },

    [actions.createFundingAccountFailure.type]: (state, action) => {
      const { payload } = action as ReturnType<typeof actions.createFundingAccountFailure>;
      action.asyncDispatch?.(actions.getFundingAccounts());
      return resourceUpserters.resetResources<FundingAccountProps, FundingAccountStoreProps>(state, payload);
    },
  },
  mkFundingAccountStore(),
);

export { FUNDING_ACCOUNTS_REDUCER_KEY };
export default fundingAccountsReducer;
